import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { settingsApi } from '../../api/settings';
import {
  CANT_ESTIMATE_RATES_LINE,
  CANT_ESTIMATE_SIZE_LINE,
  PAID_ACTIONS,
  estimateChatActionUsd,
  estimateContinueBand,
  estimatePaidActionCostUsd,
  formatChatSendEstimateLine,
  formatPaidEstimateLine,
  isChatSessionAction,
  pricingFromCatalog,
  resolvePaidModelId,
} from '../../utils/estimatePaidActionCost';
import { bandShowsRange } from '../../utils/estimateOutlineCost';
import { BETA_ALLOWANCE_USED_MESSAGE } from '../../utils/betaAllowance';
import { openSettingsAtApiKey } from '../../utils/openSettings';
import './PaidActionConfirm.css';

function AllowancePause({ onCancel }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onCancel?.();
    };
    window.addEventListener('keydown', onKey);
    const id = window.requestAnimationFrame(() => setReady(true));
    return () => {
      window.removeEventListener('keydown', onKey);
      window.cancelAnimationFrame(id);
    };
  }, [onCancel]);

  const handleBackdrop = (event) => {
    if (event.target === event.currentTarget) onCancel?.();
  };

  return createPortal(
    <div
      className="paid-confirm-backdrop"
      onClick={ready ? handleBackdrop : undefined}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10050,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: ready ? 'auto' : 'none',
      }}
    >
      <div
        className="paid-confirm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="paid-confirm-title"
        style={{
          width: '100%',
          maxWidth: '28rem',
          padding: '24px',
          background: 'var(--surface)',
          color: 'var(--text-primary)',
          border: '1px solid var(--gilt-card-border)',
        }}
      >
        <h3 id="paid-confirm-title" className="paid-confirm__title">
          Beta allowance
        </h3>
        <p className="paid-confirm__hint" data-testid="beta-allowance-pause">
          {BETA_ALLOWANCE_USED_MESSAGE}
        </p>
        <div className="paid-confirm__actions">
          <button
            type="button"
            className="paid-confirm__btn paid-confirm__btn--confirm"
            onClick={() => {
              openSettingsAtApiKey();
              onCancel?.();
            }}
          >
            Add your own key
          </button>
          <button
            type="button"
            className="paid-confirm__btn paid-confirm__btn--cancel"
            onClick={onCancel}
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Confirm before a paid AI button (#1223).
 * Same estimate line as Plot Grid Generate. Cancel spends nothing.
 * Missing rates still allow confirm. This is not a spend cap.
 */
function PaidActionConfirmLoaded({
  actionId,
  inputTokens = null,
  modelId: modelIdProp = null,
  messageCount = null,
  discovery = false,
  sizeMissing = false,
  draftCheck = null,
  title,
  hint,
  onConfirm,
  onCancel,
}) {
  const action = PAID_ACTIONS[actionId];
  const explicitModelId = typeof modelIdProp === 'string' && modelIdProp.trim()
    ? modelIdProp.trim()
    : '';
  const [modelId, setModelId] = useState(action?.defaultModelId || '');
  const [modelName, setModelName] = useState('');
  const [catalog, setCatalog] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [ratesState, setRatesState] = useState('loading');
  const [ready, setReady] = useState(false);
  const catalogRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (!catalogRef.current?.length) setRatesState('loading');
    (async () => {
      try {
        const [settingsRes, catalogRes] = await Promise.all([
          settingsApi.getSettings(),
          settingsApi.getOpenRouterCatalog().catch(() => null),
        ]);
        if (cancelled || !action) return;
        const settings = settingsRes?.data || settingsRes || {};
        setPreferences(settings?.model_preferences || {});
        const id = explicitModelId || resolvePaidModelId(settings?.model_preferences, action);
        setModelId(id);
        const models = catalogRes?.data?.models || catalogRes?.models || [];
        if (!models.length) {
          setCatalog([]);
          catalogRef.current = [];
          setModelName('');
          setRatesState('missing');
          return;
        }
        setCatalog(models);
        catalogRef.current = models;
        const entry = models.find((model) => model.id === id);
        setModelName(entry?.name || id);
        setRatesState(models.length ? 'ready' : 'missing');
      } catch {
        if (!cancelled) {
          setCatalog([]);
          catalogRef.current = [];
          setModelName('');
          setRatesState('missing');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [action, actionId, explicitModelId]);

  const sessionAction = isChatSessionAction(actionId);
  const measuredContinue = actionId === 'scene_continue';
  const size = inputTokens != null
    ? inputTokens
    : (measuredContinue ? null : action?.planningInputTokens);
  const missingSize = Boolean(sizeMissing) || (measuredContinue && size == null);
  const displayId = explicitModelId || modelId;
  const catalogEntry = Array.isArray(catalog)
    ? catalog.find((model) => model.id === displayId)
    : null;
  const pricing = pricingFromCatalog(catalog, displayId);
  const canPrice = Boolean(action) && Array.isArray(catalog) && (
    ratesState === 'ready' || (Boolean(explicitModelId) && catalog.length > 0)
  );
  let band = null;
  if (canPrice && !missingSize) {
    if (sessionAction) {
      band = estimateChatActionUsd({
        actionId,
        catalog,
        modelPreferences: preferences,
        inputTokens: size,
        replyModelId: explicitModelId || modelId,
        messageCount,
        discovery,
      });
    } else if (measuredContinue) {
      const checkModel = (draftCheck?.model_id || draftCheck?.modelId || '').trim();
      const checkInput = Number(draftCheck?.estimated_input_tokens ?? draftCheck?.estimatedInputTokens);
      const checkTypical = Number(draftCheck?.typical_output_tokens ?? draftCheck?.typicalOutput);
      const checkWorst = Number(draftCheck?.worst_output_tokens ?? draftCheck?.worstOutput);
      const checkPricing = checkModel ? pricingFromCatalog(catalog, checkModel) : null;
      const check = checkPricing && [checkInput, checkTypical, checkWorst].every(Number.isFinite)
        ? {
          pricing: checkPricing,
          inputTokens: checkInput,
          typicalOutput: checkTypical,
          worstOutput: checkWorst,
        }
        : null;
      if (draftCheck && !check) band = null;
      else band = estimateContinueBand(pricing, size, check);
    } else {
      band = estimatePaidActionCostUsd(pricing, action, size);
    }
  }
  let estimateLine = 'Loading estimate…';
  if (ratesState === 'missing' && !canPrice) estimateLine = CANT_ESTIMATE_RATES_LINE;
  else if (missingSize && ratesState !== 'loading') estimateLine = CANT_ESTIMATE_SIZE_LINE;
  else if (canPrice) {
    const line = sessionAction
      ? formatChatSendEstimateLine(band)
      : formatPaidEstimateLine(band);
    estimateLine = line || CANT_ESTIMATE_RATES_LINE;
  }
  const shownModelName = catalogEntry?.name || (explicitModelId ? displayId : modelName);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onCancel?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  useEffect(() => {
    const id = window.requestAnimationFrame(() => setReady(true));
    return () => window.cancelAnimationFrame(id);
  }, []);

  if (!action) return null;

  const handleBackdrop = (event) => {
    if (event.target === event.currentTarget) onCancel?.();
  };

  return createPortal(
    <div
      className="paid-confirm-backdrop"
      onClick={ready ? handleBackdrop : undefined}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10050,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: ready ? 'auto' : 'none',
      }}
    >
      <div
        className="paid-confirm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="paid-confirm-title"
        style={{
          width: '100%',
          maxWidth: '28rem',
          padding: '24px',
          background: 'var(--surface)',
          color: 'var(--text-primary)',
          border: '1px solid var(--gilt-card-border)',
        }}
      >
        <h3 id="paid-confirm-title" className="paid-confirm__title">
          {title || action.title}
        </h3>
        <p className="paid-confirm__hint">
          {bandShowsRange(band) ? 'A typical run costs the lower figure. A full-length one costs the higher. ' : ''}
          {hint || action.hint}
        </p>
        <p className="paid-confirm__model">
          <span className="paid-confirm__label">Model</span>
          <span>{shownModelName || (ratesState === 'loading' ? 'Loading model…' : displayId)}</span>
        </p>
        <p className="paid-confirm__estimate" data-testid="paid-action-estimate">
          {estimateLine}
        </p>
        <div className="paid-confirm__actions">
          <button
            type="button"
            className="paid-confirm__btn paid-confirm__btn--cancel"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="paid-confirm__btn paid-confirm__btn--confirm"
            onClick={onConfirm}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default function PaidActionConfirm(props) {
  if (props.paused) return <AllowancePause onCancel={props.onCancel} />;
  return <PaidActionConfirmLoaded {...props} />;
}
